/**
 * usePageLifecycle: iOS PWA page lifecycle handlers.
 *
 * Handles the three critical events described in the iOS stabilisation spec
 * (§ 4.3):
 *
 *  pagehide            iOS fires this when the WebView is suspended or
 *                      terminated. Used to flush UI state to IDB and push
 *                      any pending mutations to the server.
 *
 *  visibilitychange    hidden → flush state + push pending mutations.
 *                      visible → record forced-refresh telemetry.
 *
 * The visibilitychange visible → pull path is intentionally NOT handled here:
 * that responsibility already belongs to OfflineSyncTrigger.
 *
 * Mount as a side-effect component in App.tsx alongside the other handlers.
 */

import React, { useEffect, useRef } from 'react';
import { sessionStateService } from '../services/offline/session-state.service';
import { syncQueueService } from '../services/offline/sync-queue.service';
import { telemetryService } from '../services/offline/telemetry/telemetry-service';
import { formDraftRegistry } from '../services/offline/form-draft-registry';
import { dbBridge } from '../orm/worker/db-bridge';

function captureUIState() {
  return {
    route: window.location.pathname + window.location.search,
    scrollY: window.scrollY,
    formDrafts: formDraftRegistry.getAll(),
    timestamp: Date.now(),
  };
}

export function usePageLifecycle(): void {
  const hiddenAtRef = useRef<number | null>(null);
  const shrinkTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const onPageHide = (e: PageTransitionEvent) => {
      // Save pagehide metadata so bootstrap.ts can compute the gap after reload.
      // persisted=true means the page went into bfcache (fast back/forward);
      // persisted=false on iOS almost always = process will be/was killed.
      try {
        sessionStorage.setItem('pwa_last_pagehide', JSON.stringify({
          t: Date.now(),
          persisted: e.persisted,
          visibilityState: document.visibilityState,
        }));
      } catch { /* ignore */ }
      // Flush UI state to IDB so it can be restored after process-kill.
      sessionStateService.save(captureUIState()).catch(() => {});
      // Attempt to push any locally-queued mutations before iOS suspends us.
      syncQueueService.processQueue().catch(() => {});
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now();
        // Persist in sessionStorage so the timestamp survives a hard kill + reload.
        try { sessionStorage.setItem('pwa_last_hidden_at', String(hiddenAtRef.current)); } catch { /* ignore */ }
        sessionStateService.save(captureUIState()).catch(() => {});
        syncQueueService.processQueue().catch(() => {});

        // Release SQLite unused memory pages after 10 s in background.
        // Quick app-switches (< 10 s) are unaffected; longer backgrounding
        // reduces the OPFS worker footprint and lowers iOS memory-pressure
        // eviction risk (which would cause a full reload on re-open).
        shrinkTimerRef.current = setTimeout(() => {
          dbBridge.shrinkMemory().catch(() => {});
        }, 10_000);
      } else if (document.visibilityState === 'visible') {
        // Cancel pending shrink if the user returns quickly.
        if (shrinkTimerRef.current !== null) {
          clearTimeout(shrinkTimerRef.current);
          shrinkTimerRef.current = null;
        }

        if (hiddenAtRef.current !== null) {
          telemetryService.trackForcedRefresh({
            lastVisibilityHidden: hiddenAtRef.current,
            duration: Date.now() - hiddenAtRef.current,
          });
          hiddenAtRef.current = null;
        }
      }
    };

    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (shrinkTimerRef.current !== null) {
        clearTimeout(shrinkTimerRef.current);
      }
    };
  }, []);
}

/** Side-effect component for mounting in App.tsx alongside other handlers. */
export const PageLifecycleHandler: React.FC = () => {
  usePageLifecycle();
  return null;
};
